import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Psychology as BrainIcon,
  Nature as NatureIcon,
  Engineering as EngineeringIcon,
  Public as EarthIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  ExpandMore as ExpandIcon,
  Lightbulb as InsightIcon,
  TrendingUp as FutureIcon,
  Build as TechnicalIcon,
  Image as VisualIcon,
  Link as ReferenceIcon,
  Category as DomainIcon,
} from '@mui/icons-material';
import { ScientificAgentService } from '../services/scientificAgentService';
import { 
  ScientificSession, 
  ScientificAgentType,
  SCIENTIFIC_AGENTS,
  ScientificAgentInfo,
  AgentVisualization,
} from '../models/scientificAgent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'engineering': return <EngineeringIcon />;
    case 'life-sciences': return <NatureIcon />;
    case 'earth-sciences': return <EarthIcon />;
    case 'social-sciences': return <BrainIcon />;
    case 'physical-sciences': return <ScienceIcon />;
    default: return <ScienceIcon />;
  }
};

export const ScientificAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<ScientificAgentType>('aerospace');
  const [sessions, setSessions] = useState<ScientificSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ScientificSession | null>(null);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [detailDomain, setDetailDomain] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const allSessions = ScientificAgentService.getAllSessions();
    setSessions(allSessions);
  };

  const getAgentInfo = (agentType: ScientificAgentType): ScientificAgentInfo | undefined => {
    return SCIENTIFIC_AGENTS.find(a => a.id === agentType);
  };

  const handleStartSession = async () => {
    if (!initialPrompt.trim()) {
      setError('Please enter an initial prompt');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ScientificAgentService.startSession(selectedAgent, {
        initialPrompt,
        temperature,
        maxTokens,
      });

      if (response.success && response.sessionId) {
        const session = ScientificAgentService.createLocalSession(
          selectedAgent,
          response.sessionId,
          initialPrompt,
          response.visualization
        );

        setCurrentSession(session);
        loadSessions();
        setInitialPrompt('');
        setActiveTab(1); // Switch to visualization tab
      } else {
        setError(response.errors?.join(', ') || 'Failed to start session');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !detailDomain.trim()) {
      setError('Please select a detail domain to explore');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const agentInfo = getAgentInfo(currentSession.agentType);
      const response = await ScientificAgentService.iterate(
        currentSession.agentType,
        currentSession.sessionId,
        {
          agentType: agentInfo?.name,
          detailDomain,
          customInstructions: customInstructions || undefined,
          temperature,
          maxTokens,
        }
      );

      if (response.success && response.visualization) {
        ScientificAgentService.addVisualization(currentSession.id, response.visualization);
        setCurrentSession(ScientificAgentService.getSession(currentSession.id));
        setDetailDomain('');
        setCustomInstructions('');
      } else {
        setError(response.errors?.join(', ') || 'Failed to generate visualization');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to iterate');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: ScientificSession) => {
    setCurrentSession(session);
    setSelectedAgent(session.agentType);
    setHistoryOpen(false);
    setActiveTab(1);
  };

  const handleDeleteSession = (id: string) => {
    ScientificAgentService.deleteSession(id);
    if (currentSession?.id === id) {
      setCurrentSession(null);
      setActiveTab(0);
    }
    loadSessions();
  };

  const handleExport = () => {
    if (!currentSession) return;

    const content = ScientificAgentService.exportSession(currentSession);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scientific-${currentSession.agentType}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderVisualization = (viz: AgentVisualization, index: number) => {
    return (
      <Accordion key={index} defaultExpanded={index === 0}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, sm: 2 }, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <Typography 
              variant="h6"
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.125rem' },
                flex: isMobile ? '1 1 100%' : 'none'
              }}
            >
              {viz.title}
            </Typography>
            <Chip 
              label={viz.domain} 
              size="small" 
              color="primary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
            <Chip 
              label={viz.detailDomain} 
              size="small" 
              variant="outlined"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Overview */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    Overview
                  </Typography>
                  <Typography 
                    paragraph
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {viz.content.overview}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Detailed Explanation */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Details' : 'Detailed Explanation'}
                  </Typography>
                  <Typography 
                    paragraph 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {viz.content.detailedExplanation}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Key Terms */}
            {viz.keyTerms.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DomainIcon /> Key Terms
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {viz.keyTerms.map((term, i) => (
                        <Chip key={i} label={term} variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Key Insights */}
            {viz.content.keyInsights.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InsightIcon /> Key Insights
                    </Typography>
                    <List dense>
                      {viz.content.keyInsights.map((insight, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={insight} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Implications */}
            {viz.content.implications && viz.content.implications.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Implications</Typography>
                    <List dense>
                      {viz.content.implications.map((impl, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={impl} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Future Directions */}
            {viz.content.futureDirections && viz.content.futureDirections.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FutureIcon /> Future Directions
                    </Typography>
                    <List dense>
                      {viz.content.futureDirections.map((dir, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={dir} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Technical Details */}
            {viz.content.technicalDetails && viz.content.technicalDetails.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TechnicalIcon /> Technical Details
                    </Typography>
                    {viz.content.technicalDetails.map((detail, i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">{detail.aspect}</Typography>
                        <Typography variant="body2">{detail.explanation}</Typography>
                        {detail.importance && (
                          <Typography variant="caption" color="text.secondary">
                            Importance: {detail.importance}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Visual Elements */}
            {viz.content.visualElements && viz.content.visualElements.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VisualIcon /> Visual Elements
                    </Typography>
                    {viz.content.visualElements.map((elem, i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">{elem.type}</Typography>
                        <Typography variant="body2">{elem.description}</Typography>
                        {elem.significance && (
                          <Typography variant="caption" color="text.secondary">
                            Significance: {elem.significance}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* References */}
            {viz.content.references && viz.content.references.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReferenceIcon /> References
                    </Typography>
                    <List dense>
                      {viz.content.references.map((ref, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={ref} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const filteredAgents = selectedCategory === 'all' 
    ? SCIENTIFIC_AGENTS 
    : SCIENTIFIC_AGENTS.filter(a => a.category === selectedCategory);

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
              width: isMobile ? '100%' : 'auto',
              mb: isMobile ? 1 : 0
            }}
          >
            <ScienceIcon sx={{ fontSize: { xs: 32, sm: 40 } }} /> 
            {isMobile ? 'Science Explorer' : 'Scientific Agent Explorer'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={() => setHistoryOpen(true)} 
              title={isMobile ? undefined : "Session History"}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <Badge badgeContent={sessions.length} color="primary">
                <HistoryIcon />
              </Badge>
            </IconButton>
            {currentSession && (
              <IconButton 
                onClick={handleExport} 
                title={isMobile ? undefined : "Export Analysis"}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onChange={(_, val) => setActiveTab(val)} 
          sx={{ mb: 2 }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab 
            label={isMobile ? 'Start' : 'Select Agent & Start'} 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label={isMobile ? 'Results' : 'Visualization & Analysis'} 
            disabled={!currentSession}
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  {isMobile ? 'Select Domain' : 'Select Scientific Domain'}
                </Typography>
                
                <ToggleButtonGroup
                  value={selectedCategory}
                  exclusive
                  onChange={(_, val) => val && setSelectedCategory(val)}
                  sx={{ 
                    mb: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      minHeight: 44,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 }
                    }
                  }}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="engineering">{isMobile ? 'Eng.' : 'Engineering'}</ToggleButton>
                  <ToggleButton value="life-sciences">{isMobile ? 'Life Sci.' : 'Life Sciences'}</ToggleButton>
                  <ToggleButton value="earth-sciences">{isMobile ? 'Earth Sci.' : 'Earth Sciences'}</ToggleButton>
                  <ToggleButton value="physical-sciences">{isMobile ? 'Phys. Sci.' : 'Physical Sciences'}</ToggleButton>
                  <ToggleButton value="social-sciences">{isMobile ? 'Social Sci.' : 'Social Sciences'}</ToggleButton>
                </ToggleButtonGroup>

                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  {filteredAgents.map((agent) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          border: selectedAgent === agent.id ? 2 : 0,
                          borderColor: 'primary.main',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => setSelectedAgent(agent.id)}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography 
                              variant="h4"
                              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                            >
                              {agent.icon}
                            </Typography>
                            <Box sx={{ '& svg': { fontSize: { xs: 20, sm: 24 } } }}>
                              {getCategoryIcon(agent.category)}
                            </Box>
                          </Box>
                          <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                          >
                            {agent.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            paragraph
                            sx={{ 
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              display: isMobile ? '-webkit-box' : 'block',
                              WebkitLineClamp: isMobile ? 2 : 'none',
                              WebkitBoxOrient: 'vertical',
                              overflow: isMobile ? 'hidden' : 'visible'
                            }}
                          >
                            {agent.description}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {agent.domains.slice(0, isMobile ? 2 : 3).map((domain, i) => (
                              <Chip 
                                key={i} 
                                label={domain} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            ))}
                            {agent.domains.length > (isMobile ? 2 : 3) && (
                              <Chip 
                                label={`+${agent.domains.length - (isMobile ? 2 : 3)}`} 
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  {isMobile ? 'New Analysis' : 'Start New Analysis'}
                </Typography>

                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {isMobile ? <strong>{getAgentInfo(selectedAgent)?.name}</strong> : (
                    <>Selected Agent: <strong>{getAgentInfo(selectedAgent)?.name}</strong></>
                  )}
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  label={isMobile ? 'Prompt' : 'Initial Prompt'}
                  value={initialPrompt}
                  onChange={(e) => setInitialPrompt(e.target.value)}
                  placeholder={isMobile ? 'What to explore?' : 'Describe what you want to explore...'}
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleStartSession}
                  disabled={!initialPrompt.trim() || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                  sx={{
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {loading ? (isMobile ? 'Starting...' : 'Starting Analysis...') : (isMobile ? 'Start' : 'Start Analysis')}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  {isMobile ? 'Settings' : 'Model Settings'}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? 'Temp' : 'Temperature'}: {temperature}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, val) => setTemperature(val as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks
                    valueLabelDisplay="auto"
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? 'Tokens' : 'Max Tokens'}: {maxTokens}
                  </Typography>
                  <Slider
                    value={maxTokens}
                    onChange={(_, val) => setMaxTokens(val as number)}
                    min={512}
                    max={4096}
                    step={256}
                    marks
                    valueLabelDisplay="auto"
                  />
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {currentSession && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  position: { xs: 'static', md: 'sticky' }, 
                  top: 20 
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Explore' : 'Explore Further'}
                  </Typography>

                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {isMobile ? getAgentInfo(currentSession.agentType)?.name : (
                      <>Agent: {getAgentInfo(currentSession.agentType)?.name}</>
                    )}
                  </Alert>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {isMobile ? 'Domain' : 'Detail Domain'}
                    </InputLabel>
                    <Select
                      value={detailDomain}
                      onChange={(e) => setDetailDomain(e.target.value)}
                      label={isMobile ? 'Domain' : 'Detail Domain'}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {isMobile ? 'Select...' : 'Select a domain...'}
                      </MenuItem>
                      {getAgentInfo(currentSession.agentType)?.domains.map((domain) => (
                        <MenuItem 
                          key={domain} 
                          value={domain}
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {domain}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label={isMobile ? 'Instructions' : 'Custom Instructions (optional)'}
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder={isMobile ? 'Focus areas...' : 'Add specific instructions or focus areas...'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleIterate}
                    disabled={!detailDomain || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                    sx={{
                      minHeight: 44,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {loading ? (isMobile ? 'Analyzing...' : 'Analyzing...') : (isMobile ? 'Analyze' : 'Generate Analysis')}
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ 
                      mt: 2,
                      minHeight: 44,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                    onClick={() => {
                      setCurrentSession(null);
                      setActiveTab(0);
                    }}
                  >
                    {isMobile ? 'New Session' : 'Start New Session'}
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                {currentSession.visualizations.length === 0 ? (
                  <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <ScienceIcon sx={{ 
                      fontSize: { xs: 60, sm: 80 }, 
                      color: 'text.secondary', 
                      mb: 2 
                    }} />
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {isMobile ? 'No analyses yet' : 'No analyses yet. Select a detail domain to explore.'}
                    </Typography>
                  </Paper>
                ) : (
                  <Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      {isMobile ? 'Results' : 'Analysis Results'}
                    </Typography>
                    {currentSession.visualizations.map((viz, index) => 
                      renderVisualization(viz, index)
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Session History Dialog */}
        <Dialog 
          open={historyOpen} 
          onClose={() => setHistoryOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}>
            {isMobile ? 'Sessions' : 'Scientific Analysis Sessions'}
          </DialogTitle>
          <DialogContent>
            <List>
              {sessions.map((session) => (
                <React.Fragment key={session.id}>
                  <ListItem sx={{ py: { xs: 1.5, sm: 2 } }}>
                    <ListItemText
                      primary={`${getAgentInfo(session.agentType)?.name} - ${session.initialPrompt.slice(0, isMobile ? 30 : 50)}...`}
                      secondary={isMobile ? 
                        `${new Date(session.createdAt).toLocaleDateString()} | ${session.visualizations.length}` :
                        `Created: ${new Date(session.createdAt).toLocaleString()} | ${session.visualizations.length} analyses`
                      }
                      primaryTypographyProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        onClick={() => handleLoadSession(session)}
                        title={isMobile ? undefined : "Load session"}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <PlayIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteSession(session.id)}
                        title={isMobile ? undefined : "Delete session"}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
            <Button 
              onClick={() => setHistoryOpen(false)}
              sx={{
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};